// /src/pages/EditDiamond.jsx
import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { FaArrowLeft } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  useGetDiamondByIdQuery,
  useUpdateDiamondMutation,
} from "../toolkitServices/diamondApi";
import { useGetAllGroupedDiamondAttributesQuery } from "../toolkitServices/diamondAttributeApi";

/* ---------- UI tokens: compact + border-only validation ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const makeSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderWidth: 1,
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: "none",
    ":hover": { borderColor: hasError ? "#ef4444" : "#9481ce" },
    borderRadius: 10,
    fontSize: "0.92rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  menu: (base) => ({ ...base, zIndex: 50, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#efeafd" : state.isFocused ? "#f7f6ff" : "white",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
});

/* ---------- constants ---------- */
const DEDUCT_OPTS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const optionFromId = (opts, anyId) => {
  const idOnly =
    anyId && typeof anyId === "object"
      ? anyId._id || anyId.value || anyId.id || null
      : anyId || null;
  return idOnly ? opts.find((o) => String(o.value) === String(idOnly)) || null : null;
};

export default function EditDiamond() {
  const { id } = useParams();
  const navigate = useNavigate();

  // attributes
  const { data: grouped, isLoading: loadingAttrs, isError: attrErr } =
    useGetAllGroupedDiamondAttributesQuery();

  const shapeOptions = useMemo(
    () => (grouped?.data?.shape || []).map((a) => ({ value: a._id, label: a.name })),
    [grouped]
  );
  const colorOptions = useMemo(
    () => (grouped?.data?.color || []).map((a) => ({ value: a._id, label: a.name })),
    [grouped]
  );
  const clarityOptions = useMemo(
    () => (grouped?.data?.clarity || []).map((a) => ({ value: a._id, label: a.name })),
    [grouped]
  );

  // diamond doc
  const { data: res, isLoading: loadingDoc, isError: docErr } = useGetDiamondByIdQuery(id);
  const [updateDiamond, { isLoading: saving }] = useUpdateDiamondMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      diamondName: "",
      slug: "",
      description: "",
      deductable: { value: "yes", label: "Yes" },
      shape: null,
      color: null,
      clarity: null,
    },
    mode: "onBlur",
  });

  // hydrate when ready
  useEffect(() => {
    const d = res?.data;
    if (!d || loadingAttrs) return;

    reset({
      diamondName: d.diamondName || "",
      slug: d.slug || "",
      description: d.description || "",
      deductable: d.deductable ? DEDUCT_OPTS[0] : DEDUCT_OPTS[1],
      shape: optionFromId(shapeOptions, d.shape),
      color: optionFromId(colorOptions, d.color),
      clarity: optionFromId(clarityOptions, d.clarity),
    });

    requestAnimationFrame(() => setFocus("diamondName"));
  }, [res, loadingAttrs, reset, shapeOptions, colorOptions, clarityOptions, setFocus]);

  useEffect(() => {
    if (docErr) toast.error("Failed to load diamond");
    if (attrErr) toast.error("Failed to load attributes");
  }, [docErr, attrErr]);

  // hotkeys: Ctrl+Backspace -> back | Ctrl+S / Ctrl+Enter -> save
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.ctrlKey && e.key === "Enter")) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, handleSubmit]);

  const onSubmit = async (form) => {
    const payload = {
      diamondName: form.diamondName?.trim(),
      slug: form.slug?.trim(),
      description: form.description?.trim() || "",
      deductable: form.deductable, // keep {value,label}
      shape: form.shape,
      color: form.color,
      clarity: form.clarity,
    };
    try {
      await updateDiamond({ id, body: payload }).unwrap();
      toast.success("Diamond updated");
      navigate("/masters/diamond-master-list"); // go back to list
    } catch (e) {
      const msg = e?.data?.message || e?.data?.msg || e?.error || e?.message || "Update failed";
      toast.error(msg);
      requestAnimationFrame(() => setFocus("diamondName"));
    }
  };

  const loading = saving || loadingDoc || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Compact Header (consistent) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/masters/diamond-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Diamond List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              {loadingDoc ? "Loading…" : "Edit Diamond"}
            </h1>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact, border-only validation */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        onAnimationComplete={() => requestAnimationFrame(() => setFocus("diamondName"))}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          {/* Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Diamond Name<span className="text-red-500">*</span>
            </label>
            <input
              {...register("diamondName", { required: true, minLength: 1, maxLength: 150 })}
              aria-invalid={!!errors.diamondName}
              placeholder="e.g., Melee Mix, Necklace Diamonds"
              autoComplete="off"
              className={`${baseInput} ${errors.diamondName ? errRing : okRing}`}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Slug</label>
            <input
              {...register("slug")}
              aria-invalid={false}
              placeholder="slug-here"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Deductable */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Deductable<span className="text-red-500">*</span>
            </label>
            <Controller
              name="deductable"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable={false}
                  options={DEDUCT_OPTS}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.deductable)}
                />
              )}
            />
          </div>

          {/* Shape */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Shape<span className="text-red-500">*</span>
            </label>
            <Controller
              name="shape"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={loadingAttrs || attrErr}
                  options={shapeOptions}
                  placeholder={loadingAttrs ? "Loading…" : "Select shape"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.shape)}
                />
              )}
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Color<span className="text-red-500">*</span>
            </label>
            <Controller
              name="color"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={loadingAttrs || attrErr}
                  options={colorOptions}
                  placeholder={loadingAttrs ? "Loading…" : "Select color"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.color)}
                />
              )}
            />
          </div>

          {/* Clarity */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Clarity<span className="text-red-500">*</span>
            </label>
            <Controller
              name="clarity"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  isClearable
                  isDisabled={loadingAttrs || attrErr}
                  options={clarityOptions}
                  placeholder={loadingAttrs ? "Loading…" : "Select clarity"}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.clarity)}
                />
              )}
            />
          </div>

          {/* Description (full width) */}
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Description</label>
            <input
              {...register("description")}
              aria-invalid={false}
              placeholder="Optional description"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Actions */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 pt-1">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
            >
              {saving ? "Updating…" : "Update Diamond"}
            </motion.button>

            <Link
              to="/masters/diamond-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
